/**
 * ScenePreloader - Handles progress tracking for scene initialization AND disposal
 *
 * Managers self-register by calling preloader.register() in their constructor,
 * then call their returned completion function when initialization is done.
 * Managers can also register a dispose callback for cleanup.
 *
 * USAGE IN A MANAGER:
 *
 *   class MyManager {
 *       constructor(scene, camera, preloader) {
 *           this.scene = scene;
 *           this.camera = camera;
 *
 *           // Register for preload tracking with optional dispose callback
 *           this._preloadComplete = preloader?.register('myManager', {
 *               dispose: () => this.dispose()
 *           });
 *       }
 *
 *       async initialize() {
 *           // ... do initialization work
 *           this._preloadComplete?.();
 *       }
 *
 *       dispose() {
 *           // ... cleanup work
 *       }
 *   }
 *
 * USAGE IN MAIN.JS:
 *
 *   const preloader = new ScenePreloader((percent) => {
 *       updateProgressBar(percent);
 *   });
 *
 *   // Create managers, passing preloader
 *   this.birdManager = new BirdManager(scene, camera, worldTranslator, options, preloader);
 *   this.birdManager.initialize();
 *
 *   // Store preloader for later disposal
 *   this.scenePreloader = preloader;
 *
 *   // When unloading:
 *   this.scenePreloader.disposeAll(); // Calls dispose() on all managers in reverse order
 */
export class ScenePreloader {
    constructor(onProgress) {
        this.onProgress = onProgress;
        this.steps = new Map(); // name -> { completed: boolean, dispose: fn|null }
        this.registrationOrder = []; // Track order for reverse disposal
        this.allCompletePromise = null;
        this.allCompleteResolve = null;
    }

    /**
     * Register a preload step. Call this in manager constructor.
     * @param {string} name - Unique name for this step
     * @param {Object} [options] - Optional configuration
     * @param {Function} [options.dispose] - Cleanup function called during disposeAll()
     * @returns {Function} Call this function when initialization is complete
     */
    register(name, options = {}) {
        if (this.steps.has(name)) {
            console.warn(`[ScenePreloader] Step "${name}" already registered`);
            return () => {};
        }

        this.steps.set(name, {
            completed: false,
            dispose: options.dispose || null
        });
        this.registrationOrder.push(name);
        this._updateProgress();

        // Return completion function
        return () => {
            const step = this.steps.get(name);
            if (step && !step.completed) {
                step.completed = true;
                this._updateProgress();
                this._checkAllComplete();
            }
        };
    }

    /**
     * Synchronously complete a step (for simple sync initializations)
     * @param {string} name - Step name
     * @param {Object} [options] - Optional configuration including dispose callback
     */
    complete(name, options = {}) {
        const complete = this.register(name, options);
        complete();
    }

    /**
     * Dispose all registered managers in reverse order
     * Call this when unloading the scene
     */
    disposeAll() {
        console.log('[ScenePreloader] Disposing all managers...');

        // Dispose in reverse registration order
        for (let i = this.registrationOrder.length - 1; i >= 0; i--) {
            const name = this.registrationOrder[i];
            const step = this.steps.get(name);

            if (step?.dispose) {
                try {
                    step.dispose();
                    console.log(`[ScenePreloader] Disposed: ${name}`);
                } catch (err) {
                    console.error(`[ScenePreloader] Error disposing ${name}:`, err);
                }
            }
        }

        console.log('[ScenePreloader] All managers disposed');
    }

    /**
     * Get current progress percentage
     * @returns {number} 0-100
     */
    getProgress() {
        if (this.steps.size === 0) return 0;
        const completed = [...this.steps.values()].filter(s => s.completed).length;
        return Math.round((completed / this.steps.size) * 100);
    }

    /**
     * Wait for all registered steps to complete
     * @returns {Promise} Resolves when all steps are done
     */
    waitForAll() {
        if (this._allComplete()) {
            return Promise.resolve();
        }

        if (!this.allCompletePromise) {
            this.allCompletePromise = new Promise(resolve => {
                this.allCompleteResolve = resolve;
            });
        }

        return this.allCompletePromise;
    }

    /**
     * Reset for reuse
     */
    reset() {
        this.steps.clear();
        this.registrationOrder = [];
        this.allCompletePromise = null;
        this.allCompleteResolve = null;
        this._updateProgress();
    }

    _updateProgress() {
        if (this.onProgress) {
            this.onProgress(this.getProgress());
        }
    }

    _allComplete() {
        if (this.steps.size === 0) return false;
        return [...this.steps.values()].every(s => s.completed);
    }

    _checkAllComplete() {
        if (this._allComplete() && this.allCompleteResolve) {
            this.allCompleteResolve();
        }
    }
}
